import { useCallback, useEffect, useRef, useState } from 'react';
import { useT } from '@/i18n/runtime';
import { useSessionStore } from '@/store/sessionStore';
import { fetchSessionEntrySummary, livekitConfigured, sessionEndingOf } from '@/api';
import { useLiveCall } from '@/media/useLiveCall';
import { useGeoEvidence } from '@/media/useGeoEvidence';
import { useConsent } from '@/media/useConsent';
import { useMediaPermissions } from '@/media/useMediaPermissions';
import { useCapturePrompt } from '@/media/useCapturePrompt';
import { useIpdriftRecheck } from '@/media/useIpdriftRecheck';
import type { ConnectionState } from '@/types';
import type { StepProps } from '../types';
import { AppBar } from '../components/AppBar';
import { Button } from '../components/Button';
import { ConsentGate } from '../components/ConsentGate';
import { GeoGate } from '../components/GeoGate';
import {
  GuidedCaptureOverlay,
  isRenderablePrompt,
} from '../components/GuidedCaptureOverlay';
import { GeoRecheckOverlay, isGeoRecheckPromptKey } from '../components/GeoRecheckOverlay';
import { PermissionGate } from '../components/PermissionGate';
import { Icon } from '../components/Icon';
import { ReconnectingOverlay } from '../components/ReconnectingOverlay';

const DEV_TOOLS = import.meta.env.VITE_ENABLE_DEV_TOOLS === 'true';

/**
 * #11 (agent-drop) — how long the customer waits for a dropped/never-arrived verifier before the
 * call ends with an apology. Two minutes: long enough for a tab-reload or a short network gap on
 * the officer's side (the be admits an agent rejoin across the whole live band), short enough that
 * an anxious first-timer is not left on an endless spinner. The be's agent-drop sweeper owns the
 * server-side terminal on a matching horizon; this is the customer-facing half.
 */
const AGENT_AWAY_GRACE_MS = 120_000;
/** #11 — soften the "connecting" copy once it has taken clearly longer than usual. */
const CONNECTING_SLOW_MS = 60_000;
/**
 * ITEM 26 — how often the droom asks the SERVER whether this call is over. 5 s, matching the prompt
 * channel's cadence: one extra read on an endpoint the droom already polls, against a customer
 * otherwise stranded indefinitely. The transport signal still ends a clean call instantly; this is
 * the floor beneath it, not a replacement for it.
 */
const ENDING_WATCH_MS = 5_000;
/**
 * OWNER ROUND (customer app, defect 2) — the beat before the completion screen, so the ending reads
 * as an ending rather than the screen vanishing mid-sentence.
 */
const ENDING_BEAT_MS = 1_200;
/**
 * OWNER ROUND (customer app, defect 2) — how long we let the server settle before deciding what KIND
 * of ending this was. The be commits the status change (FINALISING on a wrap-up, ABANDONED on the
 * agent-drop sweep) BEFORE it tears the room down, so the answer is normally already there on the
 * first read; these retries exist for the read that fails or races, not as the primary mechanism.
 * A network blip must never be the reason a customer is told their verification was interrupted.
 */
const ENDING_SETTLE_MS = 2_000;
const ENDING_SETTLE_ATTEMPTS = 3;
/**
 * Connection states that mean "this call is over on this device". Nothing that watches for a LIVE
 * problem — least of all the verifier-absence grace timer — may arm itself in any of them.
 */
const TERMINAL_CONNECTIONS: ReadonlySet<ConnectionState> = new Set<ConnectionState>([
  'ended',
  'verifier_lost',
  'superseded',
  'expired',
]);

/** Map a transport ConnectionState onto the store's connection machine. */
function applyConn(state: ConnectionState): void {
  const st = useSessionStore.getState();
  switch (state) {
    case 'connecting':
      st.setConnecting();
      break;
    case 'connected':
      st.setConnected();
      break;
    case 'reconnecting':
      st.beginReconnect();
      break;
    case 'resumed':
      st.resumeInPlace();
      break;
    case 'suspended':
      st.suspend();
      break;
    case 'ended':
      st.endByServer();
      break;
    case 'expired':
      st.expire();
      break;
    case 'idle':
      break;
  }
}

/**
 * SCREEN 05 · LIVE CALL — the verifier call, on the customer's calm side.
 *
 * Two-way video: the verifier's remote track fills the stage; the customer's own
 * camera shows in the self-view PiP. Reconnection is modelled from the start: on
 * a blip the store moves to 'reconnecting' and the resume-first overlay shows
 * "progress saved · same verifier" — never a restart (UX_SPEC §3.6). Real
 * LiveKit RoomEvents feed that same machine via `applyConn` (in `useLiveCall`);
 * in demo mode (no backend) the illustrative scene renders and the call goes
 * live immediately. The customer NEVER gets recording controls (agent-only).
 */
export function LiveCall({ tenant }: StepProps): JSX.Element {
  const { t } = useT();
  const sessionId = useSessionStore((s) => s.session.sessionId) as string;
  // Never fabricate a verifier identity: show a neutral role until the server binds the
  // real claimant (NEXT_ITEMS B3). A placeholder name would misrepresent who is on the call.
  const agentName = useSessionStore((s) => s.session.agentName);
  const connection = useSessionStore((s) => s.connection);
  const advance = useSessionStore((s) => s.completeCurrentAndAdvance);
  const endForVerifierAbsence = useSessionStore((s) => s.endForVerifierAbsence);

  const configured = livekitConfigured();
  // Gate order on the call flow: CONSENT → GEO → CAMERA/MIC PERMISSION → media join.
  // S7-7 — DPDP consent, the FIRST gate and a GENUINE one: declining cannot proceed
  // (unlike geo). The real ceremony the notice-only landing was deferring to.
  const consent = useConsent(sessionId, configured);
  // S5-9 — live-location evidence, permission-primed: resolves on capture OR on an
  // honest missing state (the officer sees "location evidence missing"; it never
  // silently blocks the call). Runs only once consent has resolved.
  const geo = useGeoEvidence(sessionId, configured && consent.resolved);
  // Camera/mic permission machine (primer → prompt → guided recovery → auto-resume),
  // replacing the static "camera blocked" banner. Gates the media join.
  const perms = useMediaPermissions(configured && consent.resolved && geo.resolved);
  // §5.4 — an in-place rejoin bumps this; the hook re-mints the token and reconnects the SAME room
  // without a navigation, so consent / geo / camera permission are NOT re-run on every reconnect.
  const rejoinNonce = useSessionStore((s) => s.rejoinNonce);
  const call = useLiveCall(
    sessionId,
    configured && consent.resolved && geo.resolved && perms.canProceed,
    applyConn,
    rejoinNonce,
  );

  const [seconds, setSeconds] = useState(0);
  // S13-9: officer-prompted guided capture — busy while a shot/upload ceremony
  // is in flight (the poll pauses; a server change never yanks the UI mid-step).
  const [captureBusy, setCaptureBusy] = useState(false);
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const localVideoRef = useRef<HTMLVideoElement | null>(null);

  // Verifier identity is server-assigned at warm-handoff (the real claimant). We do NOT
  // seed a placeholder name — an unclaimed session honestly reads "Verifier" (below).
  // In demo mode there is no transport, so go live immediately for the skeleton render.
  useEffect(() => {
    if (!configured) applyConn('connected');
  }, [configured]);

  // Attach/detach the LiveKit tracks to the media elements (imperative SDK API).
  useEffect(() => {
    const el = remoteVideoRef.current;
    const t = call.remoteVideoTrack;
    if (!el || !t) return undefined;
    t.attach(el);
    return () => {
      t.detach(el);
    };
  }, [call.remoteVideoTrack]);

  useEffect(() => {
    const el = remoteAudioRef.current;
    const t = call.remoteAudioTrack;
    if (!el || !t) return undefined;
    t.attach(el);
    return () => {
      t.detach(el);
    };
  }, [call.remoteAudioTrack]);

  useEffect(() => {
    const el = localVideoRef.current;
    const t = call.localVideoTrack;
    if (!el || !t) return undefined;
    t.attach(el);
    return () => {
      t.detach(el);
    };
  }, [call.localVideoTrack]);

  /**
   * OWNER ROUND (customer app, defect 2) — **WHICH ENDING WAS THIS?**
   *
   * <p>The defect, in the owner's words, on a phone screenshot of a journey the officer had just
   * APPROVED: *"The call shouldn't be abruptly closed and there should be a thank you screen here
   * saying the process has been completed."* What the customer actually got was the INTERRUPTION
   * screen — "This call is being ended / Your verifier will contact you again to restart the
   * verification. We apologise for the inconvenience." — at the end of a journey that completed.
   *
   * <p><strong>Root cause.</strong> Three separate places decided what an ending MEANT, and none of
   * them asked the server first:
   * <ol>
   *   <li>Reaching the completion screen required {@code verifierPresence === 'present'}. On the
   *       ordinary wrap-up the officer leaves the room BEFORE it is torn down, so presence is
   *       'away' by the time 'ended' arrives and the completion branch declined to run.</li>
   *   <li>The disambiguating effect then read the session, saw FINALISING, and returned — with a
   *       comment saying the completion effect would handle it. It had already declined. Nobody
   *       advanced.</li>
   *   <li>Meanwhile the verifier-absence grace timer, armed when the officer left and never
   *       disarmed by the call ENDING, ran to its two minutes and fired the apology over the top.
   *       That is the screen in the owner's photograph.</li>
   * </ol>
   *
   * <p><strong>The fix.</strong> ONE decision, taken from the SERVER'S own status — the same
   * {@code sessionEndingOf} classification the poll below already uses. The be commits FINALISING (a
   * wrap-up) or ABANDONED (the agent-drop sweep) BEFORE it deletes the room, so by the time this
   * runs the answer exists; we simply have to ask for it instead of inferring it from who is still
   * in the room and from a timer that was measuring something else.
   *
   * <p><strong>What it refuses to do.</strong> It never advances on a status that is not a genuine
   * finish — the completion screen says the verification has been submitted for review, and a
   * FAILED/ABANDONED/RISK_REJECTED session must not be shown it. And it never apologises on a read
   * it could not make: an unreadable status is retried, not guessed. Only when the server will not
   * say, after those retries, does the honest interruption ending stand — the call IS over, and we
   * will not claim a completion we cannot evidence.
   */
  useEffect(() => {
    if (connection !== 'ended') return undefined;
    setLeftCall(false);
    setConfirmEnd(false);
    let active = true;
    let timer: number | undefined;
    let attempt = 0;
    const settle = (): void => {
      // fetchSessionEntrySummary never throws — every outcome, timeout included, is an honest summary.
      void fetchSessionEntrySummary(sessionId).then((summary) => {
        if (!active) return;
        const ending = sessionEndingOf(summary.vcipStatus);
        if (ending === 'completed') {
          timer = window.setTimeout(() => {
            if (active) advance();
          }, ENDING_BEAT_MS);
          return;
        }
        if (ending === 'terminated') {
          endForVerifierAbsence();
          return;
        }
        attempt += 1;
        if (attempt < ENDING_SETTLE_ATTEMPTS) {
          timer = window.setTimeout(settle, ENDING_SETTLE_MS);
          return;
        }
        endForVerifierAbsence();
      });
    };
    settle();
    return () => {
      active = false;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [connection, sessionId, advance, endForVerifierAbsence]);

  // ── #11 agent-drop: grace + apology ────────────────────────────────────────
  // The verifier is 'away' (dropped) or has 'never' joined while we are otherwise connected. Arm a
  // single grace timer; if they do not return within it, end THIS call with an apology (never the
  // completion screen — nothing was verified). A return ('present') clears the timer.
  const [connectingSlow, setConnectingSlow] = useState(false);
  const verifierAway = configured && call.verifierPresence === 'away';
  const verifierNeverArrived =
    configured &&
    call.verifierPresence === 'never' &&
    (connection === 'connected' || connection === 'resumed') &&
    !call.remoteConnected;
  const awaitingVerifier = verifierAway || verifierNeverArrived;
  useEffect(() => {
    if (!configured) return undefined;
    // OWNER ROUND (defect 2): a verifier who is 'away' AFTER the call has ENDED is not an absence to
    // apologise for — they are away because the call finished. Previously only 'verifier_lost' was
    // excluded here, so a completed session kept this timer running and fired the apology two minutes
    // after the officer wrapped up, over the top of a finished journey. Every terminal state disarms it.
    if (TERMINAL_CONNECTIONS.has(connection)) return undefined;
    if (!awaitingVerifier) {
      setConnectingSlow(false);
      return undefined;
    }
    const since = call.verifierAwaySince ?? Date.now();
    const elapsed = Date.now() - since;
    const softAt = Math.max(0, CONNECTING_SLOW_MS - elapsed);
    const hardAt = Math.max(0, AGENT_AWAY_GRACE_MS - elapsed);
    const soft = window.setTimeout(() => setConnectingSlow(true), softAt);
    const hard = window.setTimeout(() => {
      endForVerifierAbsence();
      call.leave();
    }, hardAt);
    return () => {
      window.clearTimeout(soft);
      window.clearTimeout(hard);
    };
  }, [
    configured,
    connection,
    awaitingVerifier,
    call.verifierAwaySince,
    endForVerifierAbsence,
    call,
  ]);

  /**
   * OWNER ITEM 26 (BUG-023) — **THE ENDING IS SERVER TRUTH, NOT A TRANSPORT INFERENCE.**
   *
   * <p>The defect, in the owner's words: *"after call end customer screen is stuck here, we must
   * either show end page or redirect to configured URL."* The screenshot shows the customer still in
   * the call view, with "your camera paused while you were away — reconnecting it now…" running,
   * long after the officer had finished.
   *
   * <p><strong>Root cause.</strong> Reaching the completion screen required `connection === 'ended'`,
   * and the ONLY producer of that state is a LiveKit `Disconnected` event carrying ROOM_DELETED or
   * PARTICIPANT_REMOVED. Every path that loses or never receives that event strands the customer
   * permanently: a tab backgrounded on a phone is already in the reconnect loop when the room is torn
   * down, so it sees no disconnect reason at all — it just retries forever, against a room that no
   * longer exists. The session-summary read that could have settled it ran ONLY once
   * `connection === 'ended'`, which is circular: it asked the server for the truth only after the
   * transport had already told us.
   *
   * <p><strong>The fix.</strong> Poll the session's own state while the call step is mounted, and let
   * the SERVER end the call. The transport remains the fast path — nothing here slows a clean ending
   * down — but it is no longer the only way out. A signal that can be missed must never be the sole
   * route to a terminal screen.
   *
   * <p><strong>What it refuses to do.</strong> Only a genuine finish (FINALISING / SESSION_COMPLETE)
   * reaches the completion screen, because that screen carries a "verified" pill. A session that ended
   * ABANDONED / FAILED / RISK_REJECTED gets the honest ending instead — over, and the RE will be in
   * touch. The droom does not narrate a rejection: that decision is the RE's to deliver, and it is not
   * the dataroom's to break to someone on a video call.
   */
  useEffect(() => {
    if (!configured || !sessionId) return undefined;
    // Already terminal for this device — nothing left to watch, and re-ending would fight the UI.
    if (TERMINAL_CONNECTIONS.has(connection)) return undefined;
    let active = true;
    const check = async (): Promise<void> => {
      const summary = await fetchSessionEntrySummary(sessionId).catch(() => null);
      // A failed read is NOT an ending. The customer stays where they are and we ask again — a
      // network blip must never close a live verification.
      if (!active || !summary) return;
      const ending = sessionEndingOf(summary.vcipStatus);
      if (!ending) return;
      call.leave();
      if (ending === 'completed') {
        advance();
      } else {
        endForVerifierAbsence();
      }
    };
    const id = window.setInterval(() => {
      void check();
    }, ENDING_WATCH_MS);
    return () => {
      active = false;
      window.clearInterval(id);
    };
  }, [configured, sessionId, connection, call, advance, endForVerifierAbsence]);

  const verifierLost = connection === 'verifier_lost';

  // Call timer — pauses while reconnecting/suspended.
  useEffect(() => {
    const live = connection === 'connected' || connection === 'resumed';
    if (!live) return undefined;
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [connection]);

  const reconnecting = connection === 'reconnecting' || connection === 'suspended';

  // ── S13-9: the droom prompt channel, polled ONLY at a safe point ────────────
  // Safe point = all pre-join gates resolved AND the transport is genuinely up
  // (connected/resumed — never mid-reconnect, never mid-consent; S4-7 defer-
  // not-interrupt). `captureBusy` additionally pauses the cadence while the
  // customer is mid-shot/mid-upload inside the overlay.
  const promptSafePoint =
    configured &&
    consent.resolved &&
    geo.resolved &&
    perms.canProceed &&
    (connection === 'connected' || connection === 'resumed');
  const promptChannel = useCapturePrompt(sessionId, promptSafePoint, captureBusy);
  // Testmap #13: the SILENT responder to an officer IP-drift re-check (be's dedicated wire —
  // ipdrift:pending → ipdrift:capture). PASSIVE class: zero customer-visible interaction, so it
  // runs whenever the transport is up (never paused for overlays — it interrupts nothing).
  useIpdriftRecheck(
    sessionId,
    configured && (connection === 'connected' || connection === 'resumed'),
  );
  // Testmap #13: prompts this customer already ANSWERED locally (fresh geo posted) but whose
  // server row may lazily complete/expire — the poll would otherwise re-serve the same pending id
  // and re-open a ceremony the customer just finished. Ids only; cleared when the prompt goes.
  const answeredPromptIds = useRef<Set<string>>(new Set());
  const servedPrompt =
    promptChannel.prompt && !answeredPromptIds.current.has(promptChannel.prompt.id)
      ? promptChannel.prompt
      : null;
  // Deploy-skew honesty: a prompted key this build cannot render is skipped
  // (never a broken overlay); the be's 3-minute expiry records the outcome.
  const activePrompt =
    servedPrompt && isRenderablePrompt(servedPrompt.primitiveKey) ? servedPrompt : null;
  // #13: the officer's location/IP re-check rides the SAME prompt plane as the photo captures —
  // rendered by its own overlay (no camera; a fresh authed geo POST is the capture).
  const activeGeoPrompt =
    servedPrompt && isGeoRecheckPromptKey(servedPrompt.primitiveKey) ? servedPrompt : null;
  // ── V116 / owner item 4: the officer's remote shutter, two ways in ──────────
  //
  // FAST PATH — the server relays the officer's (already recorded, already authorised) act into the
  // room over LiveKit's data channel, so the shutter lands in the moment the officer chose rather
  // than up to a poll-tick later. A remote shutter with five seconds of lag is not a feature.
  //
  // HONEST FALLBACK — the pending-prompt poll also carries `shutterRequestedAt`. If the data
  // message never arrives (relay hiccup, room mid-reconnect, media disabled in the deploy) the next
  // poll still fires the shutter. Slower, never lost.
  //
  // Both paths converge on a monotonic tick keyed to the CURRENT prompt: the overlay owns the
  // decision to act, and re-checks every local precondition before it touches the camera.
  const [remoteShutterAt, setRemoteShutterAt] = useState(0);
  const shutterPromptRef = useRef<string | null>(null);
  useEffect(() => {
    // A new prompt starts with a clean shutter state — a request that fired for the PREVIOUS prompt
    // must never carry over and take an unasked-for photograph for this one.
    const id = activePrompt?.id ?? null;
    if (shutterPromptRef.current !== id) {
      shutterPromptRef.current = id;
      setRemoteShutterAt(0);
    }
  }, [activePrompt?.id]);
  useEffect(() => {
    return call.subscribeRoomSignals((signal) => {
      if (signal.promptId !== shutterPromptRef.current) return; // not this prompt — ignore
      setRemoteShutterAt(Date.now());
    });
  }, [call.subscribeRoomSignals]);
  useEffect(() => {
    const requestedAt = activePrompt?.shutterRequestedAt;
    if (!requestedAt) return;
    const parsed = Date.parse(requestedAt);
    if (Number.isNaN(parsed)) return;
    // Monotonic: only ever moves forward, so the poll re-serving the same row cannot re-fire.
    setRemoteShutterAt((prev) => (parsed > prev ? parsed : prev));
  }, [activePrompt?.shutterRequestedAt]);

  const onPromptDone = useCallback(() => {
    // Completed or withdrawn — either way the local prompt clears and the call
    // resumes; the poll re-syncs against the server on its next tick.
    setCaptureBusy(false);
    promptChannel.clear();
  }, [promptChannel.clear]);
  const onGeoPromptDone = useCallback(
    (outcome: 'completed' | 'withdrawn') => {
      if (outcome === 'completed' && promptChannel.prompt) {
        answeredPromptIds.current.add(promptChannel.prompt.id);
      }
      setCaptureBusy(false);
      promptChannel.clear();
    },
    [promptChannel.prompt, promptChannel.clear],
  );
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');

  const simulateBlip = () => {
    const st = useSessionStore.getState();
    st.beginReconnect();
    // ICE-restart succeeds shortly → resume in place, nothing re-done.
    window.setTimeout(() => {
      st.resumeInPlace();
      window.setTimeout(() => st.setConnected(), 900);
    }, 2600);
  };

  // ── Ending the call, honestly ────────────────────────────────────────────
  //
  // This used to be `call.leave(); advance()` — unconditional. A customer who tapped End thirty
  // seconds in was advanced to the completion screen and told "You're verified", which is false.
  // Completion is now reachable ONLY via the server-confirmed 'ended' state (the officer finished
  // and the room was torn down). A customer-initiated leave gets a confirm step, and after it an
  // honest "you left" view whose primary action is rejoining — the room persists, the officer is
  // still on it, and the backend admits a rejoin across the whole live band.
  // Demo mode (no backend) keeps the old advance — there is no server there to confirm anything.
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [leftCall, setLeftCall] = useState(false);
  // DEC-062 C2 (13-Aug #13): the CUSTOMER's own framing is the dominant tile by default — they are
  // the one presenting documents to the camera and need to see what the officer sees. Tap to swap.
  // CSS-only swap (data-dominant) so the <video> elements and their tracks never move in the DOM.
  const [selfDominant, setSelfDominant] = useState(true);
  const swapTiles = useCallback(() => setSelfDominant((d) => !d), []);
  const rejoinNow = useSessionStore((s) => s.rejoinNow);
  const endCall = () => {
    if (!configured) {
      call.leave();
      advance();
      return;
    }
    setConfirmEnd(true);
  };
  const confirmLeave = () => {
    setConfirmEnd(false);
    setLeftCall(true);
    call.leave();
  };
  const rejoinAfterLeave = () => {
    setLeftCall(false);
    rejoinNow();
  };

  // Show the verifier's real video once subscribed; otherwise the calm scene.
  const hasRemoteVideo = call.remoteVideoTrack !== null;
  const hasLocalVideo = call.localVideoTrack !== null && call.camEnabled;

  // The pre-join gates, in order — each renders instead of the call until it
  // resolves, and the media transport stays disabled (enabled=false above) until
  // all three pass. Consent is a genuine gate (declining blocks); geo never blocks
  // (honest-missing continues); permission blocks until camera/mic are granted.
  if (configured && !consent.resolved) {
    return <ConsentGate tenant={tenant} gate={consent} />;
  }
  if (configured && !geo.resolved) {
    return <GeoGate tenant={tenant} gate={geo} />;
  }
  if (configured && !perms.canProceed) {
    return <PermissionGate tenant={tenant} perms={perms} />;
  }

  return (
    <div className="app-view" style={{ paddingBottom: 16 }}>
      <AppBar tenant={tenant} subtitle={t('droom.live.subtitle')} />

      <div
        className="call-stage"
        data-reconnect={reconnecting ? 'true' : 'false'}
        data-dominant={selfDominant ? 'self' : 'agent'}
      >
        {hasRemoteVideo ? (
          <video
            ref={remoteVideoRef}
            className="agent-video"
            autoPlay
            playsInline
            onClick={swapTiles}
          />
        ) : (
          <svg
            className="agent-scene"
            viewBox="0 0 320 460"
            preserveAspectRatio="xMidYMid slice"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="ag" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#24333f" />
                <stop offset="1" stopColor="#0e1620" />
              </linearGradient>
            </defs>
            <rect width="320" height="460" fill="url(#ag)" />
            <path d="M70 460 C70 356 116 320 160 320 C204 320 250 356 250 460 Z" fill="#3a4b52" />
            <circle cx="160" cy="222" r="60" fill="#d7b49a" />
            <path d="M104 210 C104 168 216 168 216 210 L216 196 C216 156 104 156 104 196 Z" fill="#26201d" />
            <circle cx="140" cy="220" r="5.5" fill="#26201d" />
            <circle cx="180" cy="220" r="5.5" fill="#26201d" />
            <path d="M148 250 q12 9 24 0" stroke="#9c6549" strokeWidth="3.4" fill="none" strokeLinecap="round" />
            <path d="M100 214 a60 60 0 01120 0" stroke="#1b2229" strokeWidth="7" fill="none" />
            <rect x="94" y="210" width="12" height="22" rx="5" fill="#1b2229" />
            <rect x="214" y="210" width="12" height="22" rx="5" fill="#1b2229" />
            <path d="M214 226 q16 6 12 34" stroke="#1b2229" strokeWidth="5" fill="none" />
          </svg>
        )}

        {/* Remote audio (the verifier's voice). Hidden — attached imperatively. */}
        <audio ref={remoteAudioRef} autoPlay />

        {awaitingVerifier && !verifierLost ? (
          <div className="call-waiting" role="status">
            <span className="cw-ring" aria-hidden="true" />
            {verifierAway ? (
              <>
                <div>{t('droom.call.verifierAway.title')}</div>
                <div className="cw-sub">{t('droom.call.verifierAway.body')}</div>
              </>
            ) : connectingSlow ? (
              <div>{t('droom.call.connectingSlow.body')}</div>
            ) : agentName ? (
              t('droom.call.connectingNamed', { name: agentName })
            ) : (
              t('droom.call.connecting')
            )}
          </div>
        ) : null}

        {DEV_TOOLS ? (
          <button className="blip-toggle" onClick={simulateBlip} type="button">
            {reconnecting ? 'Reconnecting…' : 'Simulate a network blip ↻'}
          </button>
        ) : null}

        <div className="call-top">
          <div className="agent-id">
            <span className="av">
              <Icon name="face" size={22} />
            </span>
            <div>
              <div className="nm">
                {agentName ? t('droom.call.verifierNamed', { name: agentName }) : t('droom.call.verifier')}
              </div>
              <div className="rl">
                {tenant.name.toUpperCase()} · {tenant.regime}
              </div>
            </div>
          </div>
          <div className="call-badges">
            <span className="timer-chip">
              <Icon name="clock" size={12} />
              <span className="mono">
                {mm}:{ss}
              </span>
            </span>
            {connection === 'connected' || connection === 'resumed' ? (
              <span className="rec-chip">
                <span className="rd" />
                {t('droom.call.rec')}
              </span>
            ) : null}
          </div>
        </div>

        <div className="script-line">
          <div className="lbl">{t('droom.call.guidance.label')}</div>
          <div className="tx">{t('droom.call.guidance.text')}</div>
        </div>

        <div className="self-pip" onClick={swapTiles}>
          {hasLocalVideo ? (
            <video ref={localVideoRef} className="self-video" autoPlay playsInline muted />
          ) : (
            <svg viewBox="0 0 74 98" width="74" height="98" aria-hidden="true">
              <rect width="74" height="98" fill="#2a3846" />
              <circle cx="37" cy="42" r="19" fill="#c99f86" />
              <path d="M12 98c0-17 12-27 25-27s25 10 25 27z" fill="#3c4a5a" />
            </svg>
          )}
        </div>

        {/* The labelled swap affordance. Stateful: the label names what tapping WILL show, and
            aria-pressed announces which tile is large. data-dominant='self' means the customer is
            large, so the action is "show the verifier large" (and vice-versa). #13: the chip was
            being covered by the full-width controls bar (same z-index, later in the DOM) — the CSS
            now lifts it clear and makes the bar transparent to taps outside its buttons. */}
        <button
          className="swap-tiles"
          type="button"
          data-testid="swap-tiles"
          aria-pressed={!selfDominant}
          onClick={swapTiles}
        >
          ⇄ {selfDominant ? t('droom.call.swapTiles.showVerifier') : t('droom.call.swapTiles.showMe')}
        </button>

        <div className="call-controls">
          <button
            className={`cc${call.micEnabled ? '' : ' off'}`}
            aria-label={call.micEnabled ? t('droom.call.mute') : t('droom.call.unmute')}
            aria-pressed={!call.micEnabled}
            onClick={call.toggleMic}
            type="button"
          >
            <Icon name="mic" size={21} />
          </button>
          <button className="cc end" aria-label={t('droom.call.end')} onClick={endCall} type="button">
            <Icon name="phone-end" size={21} />
          </button>
          <button
            className={`cc${call.camEnabled ? '' : ' off'}`}
            aria-label={call.camEnabled ? t('droom.call.camOff') : t('droom.call.camOn')}
            aria-pressed={!call.camEnabled}
            onClick={call.toggleCam}
            type="button"
          >
            <Icon name="cam" size={21} />
          </button>
        </div>

        {call.mediaError ? (
          <div className="media-note" role="alert">
            {call.mediaError}
          </div>
        ) : null}

        {/* #12 — camera paused by a background hand-off (a phone call / tab switch). Calm, amber
            (never red): it is recovering, not broken. A tap target appears only when the automatic
            re-acquire ladder gave up (a getUserMedia gesture / blocked audio playback). */}
        {!call.mediaError && (call.camState === 'lost' || call.resumeNeeded) ? (
          <div className="media-note info" role="status" aria-live="polite">
            {call.resumeNeeded ? (
              <button
                type="button"
                className="resume-media"
                data-testid="resume-media"
                onClick={call.resumeMedia}
              >
                {t('droom.call.resumeMedia.cta')}
              </button>
            ) : (
              t('droom.call.cameraLost.body')
            )}
          </div>
        ) : null}

        {/* End-call confirm — the call is a regulated interaction; leaving it is a decision,
            not a mis-tap. Calm, and honest that verification is not yet complete. */}
        {confirmEnd && !leftCall ? (
          <div className="reconnect" role="alertdialog" aria-label={t('droom.call.leave.title')}>
            <div className="rc-title">{t('droom.call.leave.title')}</div>
            <div className="rc-sub">{t('droom.call.leave.body')}</div>
            <Button variant="ghost" className="rc-rejoin" onClick={() => setConfirmEnd(false)} type="button">
              {t('droom.call.leave.stay')}
            </Button>
            <Button variant="soft" className="rc-rejoin" onClick={confirmLeave} type="button">
              {t('droom.call.leave.leave')}
            </Button>
          </div>
        ) : null}

        {/* Customer left deliberately: an honest state, never "You're verified". */}
        {leftCall && connection !== 'ended' ? (
          <div className="reconnect" role="status" aria-live="polite">
            <div className="rc-title">{t('droom.call.left.title')}</div>
            <div className="rc-sub">{t('droom.call.left.body')}</div>
            <Button variant="ghost" className="rc-rejoin" onClick={rejoinAfterLeave} type="button">
              {t('droom.call.left.rejoin')}
            </Button>
          </div>
        ) : null}

        {/* The journey was picked up on ANOTHER device (2026-08-24). Calm and terminal-for-here:
            the customer is verifying on their other device right now, so this screen offers no
            rejoin and raises no alarm — it just explains where their journey went. */}
        {connection === 'superseded' ? (
          <div className="reconnect" role="status" aria-live="polite" data-superseded="true">
            <div className="rc-title">{t('droom.call.superseded.title')}</div>
            <div className="rc-sub">{t('droom.call.superseded.body')}</div>
          </div>
        ) : null}

        {/* Terminal 'expired': previously rendered NOTHING — a frozen call stage with no message
            and no action. The session is over for this tab; say so and route somewhere honest. */}
        {connection === 'expired' ? (
          <div className="reconnect" role="status" aria-live="polite">
            <div className="rc-title">{t('droom.call.expired.title')}</div>
            <div className="rc-sub">
              {t('droom.call.expired.body', { tenantName: tenant.name })}
            </div>
          </div>
        ) : null}

        {/* #11 — the verifier dropped and did not return within the grace window. An apology, never
            a completion: nothing was verified. No rejoin (the be's sweeper is ending the session);
            the customer is told the verifier will contact them to restart. */}
        {verifierLost ? (
          <div className="reconnect" role="status" aria-live="polite" data-verifier-lost="true">
            <div className="rc-title">{t('droom.call.verifierLost.title')}</div>
            <div className="rc-sub">{t('droom.call.verifierLost.body')}</div>
            <div className="rc-sub">{t('droom.call.verifierLost.support')}</div>
          </div>
        ) : null}

        {/* Resume-first reconnect overlay (calm, never red). Suppressed once the call is lost. */}
        {!verifierLost ? <ReconnectingOverlay /> : null}

        {/* S13-9: officer-prompted guided capture — spliced at a safe point,
            over the call chrome, WITHOUT touching the LiveKit room: the overlay
            reuses the call's own published camera track for the still (see
            GuidedCaptureOverlay's camera-handoff note), so the media leg — and
            the recording — survive the capture by construction. */}
        {/* Testmap #13: the officer's location/IP re-check prompt — same plane, its own ceremony
            (no camera; the fresh authed geo POST carries the current network observation). */}
        {activeGeoPrompt && !reconnecting ? (
          <GeoRecheckOverlay
            sessionId={sessionId}
            prompt={activeGeoPrompt}
            onBusyChange={setCaptureBusy}
            onDone={onGeoPromptDone}
          />
        ) : null}

        {activePrompt && !reconnecting ? (
          <GuidedCaptureOverlay
            /* OWNER ROUND 2026-08-29 (I2): keyed per prompt. Without it a SECOND prompt served in
               the same mount (officer cancels A, fires B inside one poll window) inherits the
               first prompt's ceremony state — purpose acknowledgement, attempt count, and the
               applied-lens flags — so B's rear-camera request could be silently swallowed by A's
               spent ceremony. A state CHANGE on the same prompt (PENDING → ACKNOWLEDGED) keeps the
               same id and therefore the same mount, exactly as before. */
            key={activePrompt.id}
            sessionId={sessionId}
            prompt={activePrompt}
            localVideoTrack={call.localVideoTrack}
            camEnabled={call.camEnabled}
            onToggleCam={call.toggleCam}
            onSwitchCamera={call.switchCamera}
            onBusyChange={setCaptureBusy}
            onDone={onPromptDone}
            remoteShutterAt={remoteShutterAt}
          />
        ) : null}
      </div>
    </div>
  );
}
